import { Card, Container, Dl, Section, Stack, Status } from "@humaniti/ui";
import type { CSSProperties } from "react";
import { SEO } from "../lib/seo";

const IDENTITY_BULLETS = [
  "Four sign-in methods — email magic link, Google OIDC, phone OTP, Solana SIWS.",
  "Step-up authentication for sensitive actions (payouts, KYC, key rotation).",
  "Active session list with per-device revocation, accessible from every account page.",
  "Three KYC tiers — T1 phone, T2 ID + liveness, T3 reputation — backed by Sumsub.",
];

const INTEGRITY_BULLETS = [
  "Append-only audit chain. The audit_events, ledger_entries, and payouts tables are sha256-chained in Postgres.",
  "Mutation rejection at the database trigger layer — append-only is enforced by the database, not by the application.",
  "Single-scan tamper detection. Any in-place row edit breaks the chain hash and is detectable end-to-end.",
  "Every consequential action — sign-in, payout, role change, dataset publish, dispute outcome — writes to the audit chain.",
];

const PRIVACY_BULLETS = [
  "GDPR Art. 15 right of access — GET /api/me/export returns the full user record as portable JSON.",
  "GDPR Art. 17 right of erasure — POST /api/me/erase tombstones the account and detaches per-label receipts.",
  "Consent receipts. Every Terms, Privacy, and DPDP acceptance is versioned and timestamped per user.",
  "Data Processing Addendum (DPA) available on request for Builder accounts.",
];

const OPS_BULLETS = [
  "Sentry + OpenTelemetry → Grafana Cloud. Errors and traces in one queryable surface.",
  "FingerprintJS Pro for Sybil resistance and device clustering across the qualified pool.",
  "WCAG 2.1 AA target. axe-core runs in CI on every marketing and product build.",
  "Virtual-thread Tomcat (Project Loom). Per-request thread-per-task with kernel-thread pooling.",
];

const VENDORS: { label: string; value: string }[] = [
  { label: "Sumsub", value: "KYC + liveness checks for T2 and T3 verification." },
  { label: "Twilio", value: "Phone OTP delivery for T1 verification." },
  { label: "FingerprintJS", value: "Device fingerprinting and Sybil resistance." },
  { label: "Mailgun", value: "Transactional email — magic links, receipts, notifications." },
  { label: "Cloudflare R2", value: "Blob storage — task assets, exports, signed receipts." },
  { label: "Mongo Atlas", value: "Application data — users, orgs, projects, tasks, annotations." },
  {
    label: "Postgres host",
    value: "Audit + financial data — audit_events, ledger_entries, payouts.",
  },
  { label: "Sentry", value: "Error tracking and performance traces." },
  { label: "Grafana Cloud", value: "Metrics, logs, and OpenTelemetry traces." },
];

const SECTION_HEAD: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  fontSize: "var(--fs-h1)",
  lineHeight: "var(--lh-h1)",
  letterSpacing: "-0.01em",
};

const KICKER: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--fs-label)",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--fg-secondary)",
};

const BulletList = ({ items }: { items: string[] }) => (
  <ul
    style={{
      margin: 0,
      paddingLeft: "var(--space-5)",
      fontSize: "var(--fs-body-lg)",
      lineHeight: "var(--lh-body-lg)",
      maxWidth: 720,
    }}
  >
    {items.map((item) => (
      <li key={item} style={{ padding: "var(--space-1) 0" }}>
        {item}
      </li>
    ))}
  </ul>
);

export const TrustPage = () => (
  <>
    <SEO
      title="Trust — What we can prove"
      description="Identity, data integrity, privacy rights, operational posture, compliance roadmap. A list of facts, not marketing."
      path="/trust"
    />
    {/* 1. Lede */}
    <Section>
      <Container size="full">
        <Stack gap="2xl">
          <span style={KICKER}>§ Trust</span>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "var(--fs-display)",
              lineHeight: "var(--lh-display)",
              letterSpacing: "-0.02em",
              maxWidth: 960,
            }}
          >
            What we can prove<span style={{ color: "var(--teal)" }}>.</span>
          </h1>
          <p
            style={{
              margin: 0,
              maxWidth: 720,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              color: "var(--fg-secondary)",
            }}
          >
            Trust is a list of verifiable facts, not a marketing claim. The page below documents
            what is in production today, what is in flight, and what is deferred. Where a control is
            not yet shipped, it says so.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 2. Identity & access */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={KICKER}>§ 01 — Identity</span>
          <h2 style={SECTION_HEAD}>
            Identity and access<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <BulletList items={IDENTITY_BULLETS} />
        </Stack>
      </Container>
    </Section>

    {/* 3. Data integrity */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={KICKER}>§ 02 — Integrity</span>
          <h2 style={SECTION_HEAD}>
            Data integrity<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <BulletList items={INTEGRITY_BULLETS} />
        </Stack>
      </Container>
    </Section>

    {/* 4. Privacy rights */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={KICKER}>§ 03 — Privacy</span>
          <h2 style={SECTION_HEAD}>
            Privacy rights<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <BulletList items={PRIVACY_BULLETS} />
        </Stack>
      </Container>
    </Section>

    {/* 5. Operational posture */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={KICKER}>§ 04 — Operations</span>
          <h2 style={SECTION_HEAD}>
            Operational posture<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <BulletList items={OPS_BULLETS} />
        </Stack>
      </Container>
    </Section>

    {/* 6. Compliance roadmap */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={KICKER}>§ 05 — Roadmap</span>
          <h2 style={SECTION_HEAD}>
            What we're working toward<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <Stack gap="md" style={{ maxWidth: 720 }}>
            <Card>
              <Stack gap="sm">
                <Status variant="warning">In progress</Status>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "var(--fs-h3)",
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                  }}
                >
                  SOC 2 Type II
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: "var(--fg-secondary)",
                    fontSize: "var(--fs-body)",
                    lineHeight: "var(--lh-body)",
                  }}
                >
                  Evidence collection in progress; attestation expected within N months of launch.
                </p>
              </Stack>
            </Card>
            <Card>
              <Stack gap="sm">
                <Status variant="neutral">Deferred</Status>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "var(--fs-h3)",
                    fontFamily: "var(--font-sans)",
                    fontWeight: 500,
                  }}
                >
                  HIPAA clean-room compute
                </h3>
                <p
                  style={{
                    margin: 0,
                    color: "var(--fg-secondary)",
                    fontSize: "var(--fs-body)",
                    lineHeight: "var(--lh-body)",
                  }}
                >
                  Phase 1.1 (post-launch follow-up).
                </p>
              </Stack>
            </Card>
          </Stack>
        </Stack>
      </Container>
    </Section>

    {/* 7. Security disclosure */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="md">
          <span style={KICKER}>§ 06 — Disclosure</span>
          <h2 style={SECTION_HEAD}>
            Security disclosure<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            We run a coordinated vulnerability-disclosure process. Reports go to{" "}
            <a href="mailto:security@humaniti.io">security@humaniti.io</a>, encrypted with our
            published PGP key. Eligible scope covers production surfaces under humaniti.io. Out of
            scope — third-party vendor surfaces, denial-of-service findings, and reports depending
            on social engineering of staff. Acknowledgement within two business days; triage within
            five.
          </p>
        </Stack>
      </Container>
    </Section>

    {/* 8. Vendor list */}
    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="lg">
          <span style={KICKER}>§ 07 — Subprocessors</span>
          <h2 style={SECTION_HEAD}>
            Subprocessors<span style={{ color: "var(--teal)" }}>.</span>
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            The vendors below process data on behalf of the Network. DPAs are available on request.
          </p>
          <Dl rows={VENDORS} />
        </Stack>
      </Container>
    </Section>
  </>
);
